import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { getVisibleMenuItems } from "../helpers/contextMenu.helpers";
import { useContextMenuRoot } from "./ContextMenuRoot";
import { ContextMenuList } from "./ContextMenuList";

type ContextMenuPortalProps = {
    className?: string;
};

export function ContextMenuPortal({ className }: ContextMenuPortalProps) {
    const { isOpen, position, menu, context, closeMenu } =
        useContextMenuRoot<unknown>();

    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;

            if (!target) {
                return;
            }

            if (!menuRef.current?.contains(target)) {
                closeMenu();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };

        window.addEventListener("pointerdown", handlePointerDown);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("pointerdown", handlePointerDown);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, closeMenu]);

    if (!isOpen || !position || !menu || context === null) {
        return null;
    }

    const visibleItems = getVisibleMenuItems(menu.items, context);

    return createPortal(
        <div
            ref={menuRef}
            className={className}
            role="menu"
            style={{
                position: "fixed",
                top: position.y,
                left: position.x,
                zIndex: 1000,
            }}
        >
            <ContextMenuList
                items={visibleItems}
                context={context}
                closeMenu={closeMenu}
            />
        </div>,
        document.body,
    );
}